// Copyright (c) StrangeBee 2024

import React, { useState, ReactElement, useMemo, useCallback } from 'react';
import { Form, FormInstance, Space } from 'antd';
import { useLocalMessage } from '@Helpers/';
import { FormSubmitButton, FormSubmitOptions } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THForm } from '@Molecules/THForm/THForm';
import { FunctionInvokeOutput, FunctionMode, functionModeValues, FunctionOutput } from 'thehive-sdk';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THTextArea } from '@Molecules/Inputs/THTextArea/THTextArea';
import { FunctionEventList } from '../eventList';
import { ICONS } from '@Constants/iconsApp';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THCollapse } from '@Molecules/Inputs/THCollapse/THCollapse';
import { DisplayMetadata } from '@Molecules/DisplayMetadata/DisplayMetadata';
import { CopyValue } from '@Atoms/CopyValue/CopyValue';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { FormatDate } from '@Molecules/Format';
import { FunctionTerminal } from '../FunctionTerminal/FunctionTerminal';
import { HTMLColors } from '@Constants/colors';
import { useGenericQuery } from '@Queries/genericQueries';
import client from '@Client';
import { Ace } from 'ace-builds';
import ExternalLink from '@Atoms/ExternalLink/ExternalLink';

import './FunctionForm.scss';

export interface FunctionValuesForm extends FormSubmitOptions {
    name: string;
    mode?: FunctionMode;
    description: string;
    definition: string;
    input: string;
}

interface FunctionFormProps {
    form: FormInstance;
    defaultValues?: FunctionOutput & { multiline?: boolean };
    isValid: boolean;
    onFinish(values: FunctionValuesForm): Promise<void>;
    onChange: () => void;
    onCancel: () => void;
    eventList: FunctionEventList;
}

export const FunctionForm = ({
    form,
    defaultValues,
    isValid,
    onFinish,
    onChange,
    onCancel,
    eventList,
}: FunctionFormProps): ReactElement | null => {
    const i = useLocalMessage();

    const definition = Form.useWatch(['definition'], form);
    const input = Form.useWatch(['input'], form);
    const funcName = Form.useWatch(['name'], form);

    const [stateLoading, setStateLoading] = useState(false);
    const [testData, setTestData] = useState<FunctionInvokeOutput>();
    const [errorMessage, setErrorMessage] = useState('');
    const [std, setStd] = useState<'input' | 'stdout' | 'stderr' | 'result'>('input');

    const { data: functionDocumentation } = useGenericQuery({
        fetchCallback: () => client.FunctionController.getDocumentation(),
        key: ['get-function-documentation'],
        fetchOnStart: true,
        manual: true,
    });

    const autoCompleter = useMemo<Ace.Completer>(() => {
        const completions = (functionDocumentation?.items || []).map(({ kind, name, args }) => ({
            score: 1000,
            value: `${name}`,
            caption: `context.${name}`,
            meta: `${kind} ${args ? `(${args?.join(', ')})` : ''}`,
            name: `context.${name}`,
        }));
        return {
            // identifierRegexps: '',
            getCompletions: (
                editor: Ace.Editor,
                session: Ace.EditSession,
                position: Ace.Point,
                prefix: string,
                done: Ace.CompleterCallback,
            ) => {
                const tokens = session.getTokens(position.row);
                const isContext = tokens.find((token) => token.type === 'identifier' && token.value === 'context');
                if (isContext) {
                    return done(null, completions);
                }
            },
        };
    }, [functionDocumentation]);

    const testDataParsed = useMemo(() => {
        const duration = (testData?.durationMillis || 0) / 1000;

        try {
            const stdout = JSON.stringify(JSON.parse(testData?.stdout || ''), null, 4);

            return {
                ...testData,
                stderr: testData?.stderr || errorMessage,
                duration,
                stdout,
            };
        } catch (e) {
            return { ...testData, duration, stderr: testData?.stderr || errorMessage };
        }
    }, [testData, errorMessage]);

    const onValuesChange = (): void => {
        onChange();
    };

    const testFunction = useCallback(
        async (dryRun: boolean) => {
            let inputParsed: any = input;
            try {
                inputParsed = JSON.parse(input);
            } catch (e) {}
            try {
                const data = await eventList.onTest({
                    definition,
                    input: inputParsed,
                    dryRun,
                });

                if (data) {
                    setTestData(data);
                }
                if (data && data.result) {
                    setStd('result');
                } else {
                    setStd('stdout');
                }
                setErrorMessage('');
            } catch (e: any) {
                setErrorMessage(e.message);
                setTestData(undefined);
                setStd('stderr');
            }
        },
        [definition, eventList, input],
    );

    const functionApiUrl =
        window.location.origin +
        client.apiUrl +
        '/v1/function/' +
        (defaultValues ? encodeURIComponent(defaultValues.name) : '<name>');

    return (
        <THForm<FunctionValuesForm>
            name="function-form"
            form={form}
            onFinish={(v) => {
                setStateLoading(true);
                onFinish(v).finally(() => setStateLoading(false));
            }}
            onValuesChange={onValuesChange}
            initialValues={{
                definition: `// Your script should have a function named 'handle'\n// input is the json value that is passed when calling the script http endpoint\n// context is an object used to interact with TheHiveAPI\nfunction handle(input, context) {\n}`,
                mode: defaultValues?.mode || 'Enabled',
                ...defaultValues,
            }}
            submitTouchedValues={defaultValues !== undefined}
        >
            <THForm.Layout
                header={
                    defaultValues && (
                        <DisplayMetadata
                            items={[
                                {
                                    icon: <ICONS.SettingIcon />,
                                    label: 'id',
                                    value: <CopyValue value={defaultValues._id}>{defaultValues._id}</CopyValue>,
                                },
                                {
                                    icon: <ICONS.UserIcon />,
                                    label: i('common.created-by'),
                                    value: <AssigneeInfo assignee={defaultValues._createdBy} noAvatar />,
                                },
                                {
                                    icon: <ICONS.CreationDateIcon />,
                                    label: i('common.created-at'),
                                    value: <FormatDate date={defaultValues._createdAt} />,
                                },
                                {
                                    icon: <ICONS.UpdateDateIcon />,
                                    label: i('common.updated-at'),
                                    value: <FormatDate date={defaultValues._updatedAt} />,
                                    hidden: !defaultValues._updatedAt,
                                },
                                {
                                    icon: <ICONS.LinkOutlined />,
                                    label: '',
                                    value: <CopyValue value={functionApiUrl}>{i('general.copy-url')}</CopyValue>,
                                },
                            ]}
                        />
                    )
                }
                footer={
                    <div className="function-form__footer">
                        <THCollapse>
                            <THCollapse.Panel header={i('function.manage.test.header', { funcName })} key="terminal">
                                <FunctionTerminal
                                    defaultMode="input"
                                    duration={testDataParsed?.duration}
                                    onDryRunClick={() => testFunction(true)}
                                    onRunClick={() => testFunction(false)}
                                    hasInput
                                    modeValue={std}
                                    onModeChange={(mode) => setStd(mode)}
                                    stdout={testDataParsed?.stdout}
                                    stderr={testDataParsed?.stderr}
                                    result={testDataParsed?.result}
                                />
                            </THCollapse.Panel>

                            {!!defaultValues?.lastSuccessDetails && (
                                <THCollapse.Panel
                                    header={
                                        <Space style={{ color: HTMLColors.GREEN }} size="large">
                                            {i('function.list.columns.last-success')}
                                        </Space>
                                    }
                                    extra={<FormatDate date={defaultValues?.lastSuccessDate} defaultStyle={false} />}
                                    key="success"
                                >
                                    <FunctionTerminal
                                        defaultMode="stdout"
                                        stdout={defaultValues?.lastSuccessDetails?.stdout}
                                        stderr={defaultValues?.lastSuccessDetails?.stderr}
                                    />
                                </THCollapse.Panel>
                            )}

                            {!!defaultValues?.lastErrorDetails && (
                                <THCollapse.Panel
                                    header={
                                        <Space style={{ color: HTMLColors.RED }} size="large">
                                            {i('function.list.columns.last-error')}
                                        </Space>
                                    }
                                    extra={<FormatDate date={defaultValues?.lastErrorDate} />}
                                    key="error"
                                >
                                    <FunctionTerminal
                                        defaultMode="stderr"
                                        stdout={defaultValues?.lastErrorDetails?.stdout}
                                        stderr={`${defaultValues?.lastErrorDetails?.errorType}\n${defaultValues?.lastErrorDetails?.errorMessage}`}
                                    />
                                </THCollapse.Panel>
                            )}

                            <THCollapse.Panel
                                key="help"
                                header={i('function.manage.help.header')}
                                extra={
                                    <ExternalLink
                                        url={`${
                                            window.location.origin + client.apiUrl
                                        }/v1/docs/index.html#tag/Function`}
                                    >
                                        {i('general.documentation')}
                                    </ExternalLink>
                                }
                            >
                                <div className="function-form__footer-help">
                                    <div className="function-form__footer-help__title-section">
                                        {i('function.manage.help.curl')}
                                    </div>

                                    <pre>
                                        <code>
                                            {`curl -X POST -H 'Authorization: Bearer $API_KEY' ${functionApiUrl} -H 'Content-Type: application/json' --data '{"hello": "world"}'`}
                                        </code>
                                    </pre>

                                    <div className="function-form__footer-help__title-section">
                                        {i('function.manage.help.python')}
                                    </div>

                                    <pre>
                                        <code>
                                            {`import requests\n\ninput = {'hello': 'world'}\n\nresponse = requests.post(\n\t${functionApiUrl},\n\tjson=input,\n\theaders={ 'Authorization': f'Bearer {thehive_api_key}' }\n)`}
                                        </code>
                                    </pre>
                                </div>
                            </THCollapse.Panel>
                        </THCollapse>

                        <FormSubmitButton
                            loading={stateLoading}
                            onCancel={onCancel}
                            disabled={!isValid}
                            anotherText={i('general.save-exit')}
                            footerText={i('general.save')}
                            onAnother={
                                !!defaultValues &&
                                (() => {
                                    form.setFieldsValue({
                                        another: true,
                                    });
                                    form.submit();
                                })
                            }
                            formName="function-form"
                        />
                    </div>
                }
            >
                <THForm.Item
                    name="name"
                    label={i('function.manage.name.label')}
                    rules={[
                        {
                            required: true,
                            message: i('function.manage.name.required'),
                        },
                    ]}
                >
                    <THInput placeholder={i('function.manage.name.placeholder')} disabled={!!defaultValues} />
                </THForm.Item>

                <THForm.Item name="mode" label={i('function.manage.mode.label')}>
                    <THSelect options={functionModeValues.map((value) => ({ label: value, value }))} />
                </THForm.Item>

                <THForm.Item name="description" label={i('function.manage.description.label')}>
                    <THTextArea
                        rows={2}
                        className="function-form__textarea"
                        placeholder={i('function.manage.description.placeholder')}
                    />
                </THForm.Item>

                <div className="function-form__definition">
                    <THTextArea.Helper
                        name="definition"
                        label={i('function.manage.definition.label')}
                        placeholder=""
                        rules={[
                            {
                                required: true,
                                message: i('function.manage.definition.required'),
                            },
                        ]}
                        valueFormat="javascript"
                        hideHelper
                        autoCompleter={autoCompleter}
                        enableAutoCompletion={true}
                    />
                </div>
            </THForm.Layout>
        </THForm>
    );
};
